//----------------------------------------------------------------------------------------------------------------------
// Trigger Binding
//----------------------------------------------------------------------------------------------------------------------

import type { GameEventBus } from '../eventBus.ts';

// Interfaces
import type { Action } from '../../interfaces/action.ts';
import type { Binding, TriggerBindingDefinition } from '../../interfaces/binding.ts';
import type { DeviceType, DeviceValueReader, InputState } from '../../interfaces/input.ts';

//----------------------------------------------------------------------------------------------------------------------

/**
 * All supported edge modes for validation
 */
export const edgeModes = [ 'rising', 'falling', 'both' ] as const;

/**
 * Defines when a trigger binding will fire based on input state changes
 */
export type EdgeMode = typeof edgeModes[number];

/**
 * Options for configuring a trigger binding
 */
export interface TriggerBindingOptions
{
    /**
     * Which edge(s) should cause the trigger to fire
     * - 'rising': Only trigger on false -> true transitions
     * - 'falling': Only trigger on true -> false transitions
     * - 'both': Trigger on any state change
     */
    edgeMode ?: EdgeMode;

    /**
     * Threshold for analog inputs (0.0 to 1.0)
     * When input value is >= threshold, it's considered "active" (true)
     * When input value is < threshold, it's considered "inactive" (false)
     * Defaults to 0.5 for analog inputs
     */
    threshold ?: number;

    /**
     * Optional context name this binding belongs to
     */
    context ?: string;
}

//----------------------------------------------------------------------------------------------------------------------

/**
 * Implementation of a trigger binding, which emits an action when a button/key is pressed or released.
 * Can handle both digital and analog inputs with configurable threshold.
 * Can output either digital or analog values based on the action type.
 */
export class TriggerBinding implements Binding
{
    public readonly type = 'trigger' as const;
    public readonly action : Action;
    public readonly context ?: string;
    public readonly deviceID : string;
    public readonly deviceType : DeviceType;
    public readonly reader : DeviceValueReader;

    // Trigger-specific options
    private readonly _edgeMode : EdgeMode;
    private readonly _threshold : number;

    // State tracking
    private _lastDigitalState = false;

    //------------------------------------------------------------------------------------------------------------------
    // Public Getters
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Get the current options for this trigger binding.
     */
    get options() : TriggerBindingOptions
    {
        return {
            edgeMode: this._edgeMode,
            threshold: this._threshold,
        };
    }

    //------------------------------------------------------------------------------------------------------------------

    /**
     * Create a new trigger binding
     *
     * @param action
     * @param deviceID
     * @param deviceType
     * @param reader
     * @param options
     */
    constructor(
        action : Action,
        deviceID : string,
        deviceType : DeviceType,
        reader : DeviceValueReader,
        options : TriggerBindingOptions = {}
    )
    {
        this.action = action;
        this.deviceID = deviceID;
        this.deviceType = deviceType;
        this.reader = reader;
        this.context = options.context;

        // Set options with defaults
        this._edgeMode = options.edgeMode ?? 'rising';
        this._threshold = options.threshold ?? 0.5;
    }

    //------------------------------------------------------------------------------------------------------------------
    // Public Methods
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Process input state and emit an action event on the configured edge transition(s).
     *
     * @param state
     * @param eventBus
     */
    public process(state : InputState, eventBus : GameEventBus) : void
    {
        // Extract the raw value using our source
        const rawValue = this.reader.getValue(state) ?? false;

        // Determine the digital state based on the threshold
        const digitalState = typeof rawValue === 'boolean'
            ? rawValue
            : rawValue >= this._threshold;

        // Determine if we should trigger based on the configured edge mode
        let shouldTrigger = false;

        switch (this._edgeMode)
        {
            case 'rising':
                shouldTrigger = digitalState && !this._lastDigitalState;
                break;
            case 'falling':
                shouldTrigger = !digitalState && this._lastDigitalState;
                break;
            case 'both':
                shouldTrigger = digitalState !== this._lastDigitalState;
                break;
        }

        // Update last known state for next time
        this._lastDigitalState = digitalState;

        // If triggered, emit the action event
        if(shouldTrigger)
        {
            let outputValue : boolean | number;
            if(this.action.type === 'analog')
            {
                // Use raw value and convert to a number if needed
                let finalValue = typeof rawValue === 'number' ? rawValue : (rawValue ? 1.0 : 0.0);

                // Apply scaling if action parameters exist
                if(this.action.minValue !== undefined || this.action.maxValue !== undefined)
                {
                    const min = this.action.minValue ?? 0;
                    const max = this.action.maxValue ?? 1;
                    finalValue = min + (finalValue * (max - min));
                }
                outputValue = finalValue;
            }
            else
            {
                outputValue = true;
            }
            const actionEvent = {
                type: `action:${ this.action.name }`,
                payload: {
                    value: outputValue,
                    deviceId: this.deviceID,
                    context: this.context,
                },
            };
            eventBus.publish(actionEvent);
        }
    }

    /**
     * Reset edge-detection state for a context activation. When a state is provided, primes from the current physical
     * input so that already-held inputs don't produce phantom rising edges in the newly activated context.
     */
    public resetEdgeState(state ?: InputState) : void
    {
        if(state)
        {
            const rawValue = this.reader.getValue(state) ?? false;
            this._lastDigitalState = typeof rawValue === 'boolean'
                ? rawValue
                : rawValue >= this._threshold;
        }
        else
        {
            this._lastDigitalState = false;
        }
    }

    /**
     * Returns a JSON-serializable representation of this trigger binding.
     */
    public toJSON() : TriggerBindingDefinition
    {
        return {
            type: this.type,
            action: this.action.name,
            input: {
                deviceID: this.deviceID,
                ...this.reader.toJSON(),
            },
            context: this.context,
            options: this.options,
        };
    }
}

//----------------------------------------------------------------------------------------------------------------------
